import React from "react";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { useQuery } from "react-query";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ExternalLink,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const OverviewLeft = () => {
  const { t } = useTranslation();
  const axiosInstance = useAxiosInstance();

  const fetchOverview = async () => {
    const response = await axiosInstance.get(`api/widgetDemo/getOverview`);
    return response?.data?.data;
  };

  const { data: overview, isLoading, isError } = useQuery(
    ["overviewData"],
    fetchOverview,
    { refetchOnWindowFocus: false }
  );

  const last7Days = overview?.last7Days || {};
  const grievances = last7Days?.grievances || {};
  const calendarEntries = last7Days?.calendarEntries || {};
  const tasks = last7Days?.tasks || 0;
  const totalGrievances = last7Days?.totalGrievances;
  const totalSurvey = last7Days?.totalSurvey
  
  // Social media data
  const socialStats = [
    {
      platform: t("Dashboard.facebook"),
      icon: <Facebook className="text-[#004874]" size={18} />,
      trend: "+5%",
      followers: "10.2K",
      engagement: "1.2K",
    },
    {
      platform: t("Dashboard.twitter"),
      icon: <Twitter className="text-[#004874]" size={18} />,
      trend: "+3%",
      followers: "8.4K",
      engagement: "900",
    },
    {
      platform: t("Dashboard.instagram"),
      icon: <Instagram className="text-[#004874]" size={18} />,
      trend: "+7%",
      followers: "12.1K",
      engagement: "1.5K",
    },
    {
      platform: t("Dashboard.linkedin"),
      icon: <Linkedin className="text-[#004874]" size={18} />,
      trend: "+2%",
      followers: "6.3K",
      engagement: "600",
    },
  ];

  const fetchGmsSummary = async () => {
    const response = await axiosInstance.get(
      `api/grievances/getGrievanceSummary`
    );
    const gmsSummary = response?.data?.data;
    return gmsSummary;
  };
  const gmsPriorityData = useQuery(["gmsSummary"], fetchGmsSummary, {
    refetchOnWindowFocus: false,
  });
  // Access the priority counts from the API response based on _id
  const priorityCounts = {};
  gmsPriorityData?.data?.forEach((priority) => {
    const priorityId = priority?._id;
    const count = priority?.count;
    priorityCounts[priorityId] = count;
  });

  const fetchGmsStatusSummary = async () => {
    const response = await axiosInstance.get(
      `api/grievances/getGrievanceStatusSummary`
    );
    const gmsStatus = response?.data?.data;
    return gmsStatus;
  };

  const gmsSummaryData = useQuery(["gmsStatus"], fetchGmsStatusSummary, {
    refetchOnWindowFocus: false,
  });

  const statusCounts = {
    unassigned: 0,
    assigned: 0,
    inProgress: 0,
    pendingApproval: 0,
    complete: 0,
  };

  gmsSummaryData?.data?.forEach((status) => {
    const statusId = status?._id;
    const statusCount = status?.count;
    statusCounts[statusId] = statusCount;
  });

  return (
    <>
      <div className="lg:col-span-1 flex flex-col space-y-4 h-[100%]">
        {/* Last 7 days Records */}
        <div className="card-wrapper">
          <div className="flex-between">
            <h3 className="card-heading">
               {t("Dashboard.last7DaysRecords")}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-3 mb-2">
            {/* Total Grievances Card */}
            <div className="card-box card-box-md md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2">
              <div className="flex-between">
                <div>
                  <p className="font-semibold text-sm sm:text-base md:text-md xl:text-md">{t("Dashboard.totalGrievances")}</p>
                </div>
                <NavLink to="/gms" className="hover:text-blue-600">
                    <button className="p-1 sm:p-2 hover:bg-white/20 rounded transition-all duration-200">
                      <ExternalLink size={15} className="" />
                    </button>
                </NavLink>
              </div>
              <div className="flex-between flex-wrap">
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.unassigned")}</span>
                  <div className="text-center heading-white text-sm sm:text-base md:text-md xl:text-md font-bold">
                    {/* {statusCounts["unassigned"]} */}
                    {grievances?.unassigned}
                  </div>
                </div>
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.assigned")}</span>
                  <div className="text-center heading-white text-sm sm:text-base md:text-md xl:text-md font-bold">
                    {grievances?.assigned}
                  </div>
                </div>
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.inProgress")}</span>
                  <div className="text-center heading-white text-sm sm:text-base md:text-md xl:text-md font-bold">
                    {grievances?.inProgress}
                  </div>
                </div>
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.competed")}</span>
                  <div className="text-center heading-white text-sm sm:text-base md:text-md xl:text-md font-bold">
                    {grievances?.completed}
                  </div>
                </div>
              </div>
            </div>
            {/* Calender Entries */}
            <div className="card-box card-box-sm">
              <div className="flex justify-center items-center">
                <div>
                  <p className="font-semibold text-sm sm:text-base md:text-sm xl:text-sm">{t("Dashboard.calendarEntries")}</p>
                </div>
                {/* <button className="p-1 hover:bg-white/20 rounded transition-all duration-200">
                  <ExternalLink size={14} />
                </button> */}
              </div>
              <div className="flex justify-between text-xs sm:text-sm md:text-base gap-2 sm:gap-4">
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.pending")}</span>
                  <div className="heading-white text-center font-bold text-sm sm:text-base md:text-lg xl:text-xl">{calendarEntries?.pending}</div>
                </div>
                <div>
                  <span className="text-[0.6rem] sm:text-[0.7rem] md:text-md subheading">{t("Dashboard.approved")}</span>
                  <div className="heading-white text-center font-bold text-sm sm:text-base md:text-lg xl:text-xl">{calendarEntries?.approved}</div>
                </div>
              </div>
            </div>

            {/* GMS Entries Card */}
            <div className="card-box card-box-sm">
              <div className="flex justify-center items-center mb-2">
                <div>
                  <p className="font-semibold text-sm sm:text-base md:text-sm xl:text-sm">
                    {t("Dashboard.gmsEntries")}
                  </p>
                </div>
              </div>
              <p className="heading-white text-sm sm:text-base md:text-lg xl:text-xl font-bold text-center">{totalGrievances}</p>
            </div>

            <div className="card-box card-box-sm flex flex-col justify-between items-center">
              <div className="flex-between mb-2">
                <div>
                  <p className="font-semibold text-sm sm:text-base md:text-sm xl:text-sm">{t("Dashboard.totalTasks")}</p>
                </div>
                {/* <button className="p-2 hover:bg-white/20 rounded transition-all duration-200">
                  <ExternalLink size={14} />
                </button> */}
              </div>
              <p className="heading-white text-sm sm:text-base md:text-lg xl:text-xl font-bold">{tasks}</p>
            </div>

            {/* Survey Reports Card */}
            <div className="card-box card-box-sm flex flex-col justify-between items-center">
              <div className="flex-between mb-2">
                <div>
                  <p className="font-semibold text-sm sm:text-base md:text-sm xl:text-sm">
                    {t("Dashboard.totalSurvey")}
                  </p>
                </div>
                {/* <button className="p-2 hover:bg-white/20 rounded transition-all duration-200">
                  <ExternalLink size={14} />
                </button> */}
              </div>
              <p className="heading-white text-sm sm:text-base md:text-lg xl:text-xl text-center font-bold">{totalSurvey}</p>
            </div>
          </div>
        </div>
        {/* Social Media */}
        <div className="card-wrapper">
          <div className="flex-between">
            <h3 className="flex items-center gap-2 card-heading">
              {t("Dashboard.socialMediaAnalytics")}
            </h3>
            <div className="flex space-x-2">
              <NavLink to="/socialmedia" className="hover:text-blue-600">
                <button className="p-1 hover:bg-white/20 rounded text-gray-600 transition-all duration-200">
                  <ExternalLink size={16} />
                </button>
              </NavLink>
            </div>
          </div>

          {/* Social Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-2 sm:gap-3 md:gap-4 mb-3">
            {socialStats.map((social, index) => (
              <div
                key={index}
                className="card-box card-box-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm md:text-base flex gap-1 items-center">
                    <span className="w-4 h-4">{social.icon}</span>
                    <span className="text-xs sm:text-sm md:text-sm font-medium subheading">{social.platform}</span>
                  </div>
                  <span className="text-xs sm:text-xs text-green-600 font-semibold">
                    {social.trend}
                  </span>
                </div>
                <div className="text-sm sm:text-base md:text-md xl:text-lg font-bold heading-white">
                  {social.followers}
                </div>
                <div className="text-xs sm:text-xs subheading">
                  {social.engagement} engagement
                </div>
              </div>
            ))}
          </div>

          {/* Total Reach Summary */}
          <div className="card-box card-box-md">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-xs sm:text-sm md:text-sm font-semibold heading">{t("Dashboard.totalReach")}</h4>
                <div className="text-base sm:text-md md:text-lg xl:text-xl font-bold heading">42.4K</div>
              </div>
              <div className="text-right">
                <div className="text-xs sm:text-xs text-green-600 font-semibold">
                  +10.2%
                </div>
                <div className="text-xs sm:text-xs subheading">vs last month</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewLeft;
